import { useCallback, useEffect, useRef } from 'react';
import useClassroomStore from '../../stores/classroomStore';

const DRIFT_THRESHOLD = 0.6;
const HEARTBEAT_MS = 2000;

/**
 * Sync logic shared between SyncedVideo and SyncedAudio.
 * Teacher fires `sendVideoState` on play/pause/seek events.
 * Students mirror the latest server state, snapping currentTime when drift > threshold
 * and extrapolating the teacher's playhead between updates so playback stays close.
 *
 * Mute/volume are intentionally NOT synced — each browser session controls its own
 * audio output, so the teacher muting locally does not silence the student.
 *
 * Returns:
 *   mediaRef    — attach to the <video>/<audio>
 *   teacherProps — onPlay/onPause/onSeeked handlers (only when isTeacher)
 */
export default function useSyncedMedia({ elementId, isTeacher, lessonSessionId }) {
  const mediaRef = useRef(null);
  const lastAppliedAnchor = useRef(0);

  const sendVideoState = useClassroomStore((s) => s.sendVideoState);
  const remoteState = useClassroomStore((s) => s.syncedVideoStates?.[elementId]);

  const pushTeacherState = useCallback((overrides = {}) => {
    if (!isTeacher || !lessonSessionId) return;
    const m = mediaRef.current;
    if (!m) return;
    sendVideoState(lessonSessionId, elementId, {
      isPlaying: !m.paused,
      currentTime: m.currentTime || 0,
      ...overrides
    });
  }, [isTeacher, lessonSessionId, elementId, sendVideoState]);

  // Apply remote state on the student side. Runs on every poll (remoteState
  // is a fresh object each fetch), so both drift correction AND play/pause
  // reconciliation happen continuously — critical for recovering from
  // network hiccups where the student's video paused for buffering but the
  // teacher hasn't sent a new event.
  useEffect(() => {
    if (isTeacher) return;
    const m = mediaRef.current;
    if (!m || !remoteState) return;

    const anchor = remoteState.anchorAt || 0;
    const elapsed = remoteState.isPlaying && anchor
      ? (Date.now() - anchor) / 1000
      : 0;
    const expected = (remoteState.currentTime || 0) + elapsed;

    if (Math.abs((m.currentTime || 0) - expected) > DRIFT_THRESHOLD) {
      try { m.currentTime = expected; } catch (_) {}
    }

    if (remoteState.isPlaying && m.paused) {
      m.play().catch(() => {
        // Unmuted autoplay was blocked (student had no user gesture yet).
        // Fall back to muted play so at least the frames advance in sync with
        // the teacher; the student's next click anywhere will unmute automatically
        // once the browser considers the tab activated.
        try {
          m.muted = true;
          m.play().catch(() => {});
        } catch (_) {}
      });
    } else if (!remoteState.isPlaying && !m.paused) {
      m.pause();
    }

    lastAppliedAnchor.current = anchor;
  }, [remoteState, isTeacher]);

  // Teacher heartbeat: push fresh (currentTime, anchor) every HEARTBEAT_MS
  // while playing. Without this, the student extrapolates from a stale anchor
  // and drift compounds — especially over long videos or flaky networks.
  useEffect(() => {
    if (!isTeacher || !lessonSessionId) return;
    const interval = setInterval(() => {
      const m = mediaRef.current;
      if (!m || m.paused || m.ended) return;
      pushTeacherState();
    }, HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, [isTeacher, lessonSessionId, pushTeacherState]);

  // Autoplay policy can force the student's media to start muted (see fallback
  // above). Students have no controls to unmute manually, so we honor the
  // fallback's promise — the first user gesture anywhere in the lesson unmutes
  // the element. Teachers own their own controls, so this only runs student-side.
  useEffect(() => {
    if (isTeacher) return;
    const unmuteOnGesture = () => {
      const m = mediaRef.current;
      if (m && m.muted) m.muted = false;
    };
    const opts = { capture: true };
    document.addEventListener('pointerdown', unmuteOnGesture, opts);
    document.addEventListener('touchstart', unmuteOnGesture, opts);
    document.addEventListener('keydown', unmuteOnGesture, opts);
    return () => {
      document.removeEventListener('pointerdown', unmuteOnGesture, opts);
      document.removeEventListener('touchstart', unmuteOnGesture, opts);
      document.removeEventListener('keydown', unmuteOnGesture, opts);
    };
  }, [isTeacher]);

  const teacherProps = isTeacher
    ? {
        onPlay: () => pushTeacherState({ isPlaying: true }),
        onPause: () => pushTeacherState({ isPlaying: false }),
        onSeeked: () => pushTeacherState()
      }
    : {};

  return { mediaRef, teacherProps };
}
